import { Link } from "react-router-dom";
import { Sprout, Leaf, ShieldCheck, Sparkles, Droplets, Check, Star, ArrowRight } from "lucide-react";
import { MaskedLines, Reveal, Eyebrow } from "../components/motion";
import { BuyBox } from "../components/BuyBox";
import { ResultsSection } from "../components/ResultsSection";
import { ProductCard } from "../components/ProductCard";
import { useProduct, useProducts } from "../hooks/useProducts";

const BENEFITS = [
  { icon: ShieldCheck, text: "Aiuta a ridurre la caduta eccessiva" },
  { icon: Sprout, text: "Supporta la fase di crescita del capello (anagen)" },
  { icon: Sparkles, text: "Aiuta a migliorare l'aspetto delle zone diradate" },
  { icon: Droplets, text: "Aiuta a riequilibrare il cuoio capelluto senza aggredirlo" },
];

const INGREDIENTS = [
  {
    name: "Oleum Rosmarini Officinalis",
    desc: "Ricco di acido rosmarinico, viene utilizzato nella cosmetica per le sue proprietà legate alla cura del cuoio capelluto e alla microcircolazione cutanea.",
    highlight: "Supporta la vitalità del cuoio capelluto e il benessere del follicolo.",
  },
  {
    name: "Botanical Complex ricco in Thymoquinone",
    desc: "Attivo botanico dalle proprietà riequilibranti, pensato per prendersi cura della cute stressata.",
    highlight: "Aiuta a mantenere il cuoio capelluto equilibrato e a prendersi cura dei capelli indeboliti.",
  },
  {
    name: "Olio di Prunus Amygdalus Dulcis",
    desc: "Fonte naturale di vitamina E e acidi grassi essenziali.",
    highlight: "Nutre, ammorbidisce e aiuta a mantenere i capelli più forti e dall'aspetto sano.",
  },
];

const CHECKLIST = [
  "Noti una caduta dei capelli particolarmente intensa",
  "Hai notato un progressivo diradamento",
  "Le tempie o l'attaccatura appaiono più vuote",
  "I tuoi capelli appaiono sottili e fragili",
  "Vuoi inserire nella tua routine una formula di origine botanica",
  "Vuoi prenderti maggiore cura del tuo cuoio capelluto",
];

const STEPS = [
  {
    title: "Applica",
    text: "Applica il prodotto direttamente sul cuoio capelluto, concentrandoti sulle zone interessate.",
    img: "/steps/step-1.png",
  },
  {
    title: "Massaggia",
    text: "Massaggia delicatamente per circa 10 minuti con le dita o con una Scalp Brush: il massaggio stimola la microcircolazione e distribuisce il prodotto.",
    img: "/steps/step-2.png",
  },
  {
    title: "Lascia agire",
    text: "Lascia in posa fino a 3 ore, quindi risciacqua effettuando due passate di shampoo.",
    img: "/steps/step-3.png",
  },
];

const TIPS = [
  "Consistenza leggera",
  "Non unge se utilizzato correttamente",
  "Non è necessario utilizzare molto prodotto",
  "La costanza è fondamentale per seguire la routine",
];

const FORMULA = [
  "Senza siliconi",
  "Senza parabeni",
  "Attivi di origine naturale",
  "Non testato su animali",
  "Prodotto artigianale",
];

const Chapter = ({ number, eyebrow, title, children, className = "", titleClass = "text-plum" }) => (
  <section className={`py-14 sm:py-20 ${className}`}>
    <div className="max-w-7xl mx-auto px-6">
      <Reveal>
        <div className="flex items-baseline gap-4">
          <span className="font-serif text-5xl sm:text-6xl text-lilac-light select-none">{number}</span>
          <div>
            <Eyebrow>{eyebrow}</Eyebrow>
            <h2 className={`mt-2 font-serif text-2xl sm:text-3xl lg:text-4xl ${titleClass} tracking-tight`}>{title}</h2>
          </div>
        </div>
      </Reveal>
      <div className="mt-10">{children}</div>
    </div>
  </section>
);

export default function TreatmentPage() {
  const { product } = useProduct("trattamento-rapida-crescita");
  const { products } = useProducts();
  const kits = products.filter((p) => p.category === "kit");

  if (!product) return <main className="py-32 text-center text-plum/50">Caricamento...</main>;

  return (
    <main data-testid="treatment-page">
      {/* OVERVIEW */}
      <section className="bg-gradient-to-b from-lilac-soft/70 to-transparent py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative">
              <div className="rounded-[2.5rem] rounded-tl-[7rem] overflow-hidden border-4 border-white shadow-[0_40px_80px_-30px_rgba(46,16,101,0.3)]">
                <img src={product.image} alt={product.name} className="w-full aspect-square object-cover" data-testid="treatment-image" />
              </div>
              <span className="absolute top-4 right-4 bg-lilac text-white text-[10px] font-bold uppercase tracking-[0.14em] px-4 py-2 rounded-full">
                Il prodotto principale
              </span>
            </div>
          </Reveal>
          <div>
            <Eyebrow>Trattamento cuoio capelluto</Eyebrow>
            <MaskedLines
              as="h1"
              delay={0.1}
              lines={["Riduce la caduta.", "Stimola la ricrescita.", "Rinforza. Riequilibra."]}
              className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-plum tracking-tight leading-[1.15]"
            />
            <Reveal delay={0.5}>
              <p className="mt-5 text-base sm:text-lg text-plum/60 leading-relaxed">
                Una formula botanica ad alta efficacia che agisce direttamente sul cuoio capelluto,
                pensata per accompagnare il capello durante il suo naturale ciclo di crescita e per
                prendersi cura delle zone soggette a caduta e diradamento.
              </p>
              <div className="mt-8 bg-white rounded-3xl border border-lilac-light p-6 sm:p-8 shadow-[0_20px_60px_-30px_rgba(139,92,246,0.4)]">
                <BuyBox product={product} ctaLabel="Acquista il trattamento" />
                <p className="mt-3 text-xs text-plum/50 flex items-center gap-1.5">
                  <Droplets className="w-3.5 h-3.5" /> 50 ml • Circa 1 mese di utilizzo
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BENEFICI */}
      <Chapter number="01" eyebrow="I benefici" title="Cosa può fare per i tuoi capelli?" className="bg-lilac-soft/50 texture-grain">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {BENEFITS.map((b, i) => (
            <Reveal key={b.text} delay={i * 0.07}>
              <div className="bg-white rounded-3xl p-6 h-full border border-transparent hover:border-lilac-light hover:-translate-y-1 transition-all duration-500">
                <span className="w-11 h-11 rounded-2xl bg-lilac-soft flex items-center justify-center">
                  <b.icon className="w-5 h-5 text-matcha" />
                </span>
                <p className="mt-4 text-sm font-medium text-plum leading-relaxed">{b.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-8 text-xs text-plum/50 max-w-2xl">
            Il Trattamento Rapida Crescita è un cosmetico per la cura del cuoio capelluto: non è un farmaco né una cura medica.
          </p>
        </Reveal>
      </Chapter>

      {/* FORMULA BOTANICA */}
      <Chapter number="02" eyebrow="Gli attivi" title="La forza della formula botanica">
        <p className="text-base sm:text-lg text-plum/60 max-w-2xl leading-relaxed -mt-4 mb-10">
          Una combinazione di attivi botanici selezionati per prendersi cura del tuo cuoio capelluto e dei tuoi capelli.
        </p>
        <div className="space-y-5">
          {INGREDIENTS.map((ing, i) => (
            <Reveal key={ing.name} delay={i * 0.08}>
              <div className="grid md:grid-cols-[auto_1fr] gap-6 bg-mist rounded-3xl p-7 sm:p-9 items-start">
                <span className="w-14 h-14 rounded-full bg-plum text-lilac-light font-serif text-xl flex items-center justify-center shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="font-serif text-xl sm:text-2xl text-plum uppercase tracking-wide">{ing.name}</h3>
                  <p className="mt-3 text-sm sm:text-base text-plum/60 leading-relaxed">{ing.desc}</p>
                  <p className="mt-3 text-sm sm:text-base text-matcha italic flex gap-2 leading-relaxed">
                    <Leaf className="w-4 h-4 shrink-0 mt-1" /> {ing.highlight}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Chapter>

      {/* È GIUSTO PER TE? */}
      <Chapter number="03" eyebrow="Autodiagnosi" title="È il trattamento giusto per te?" className="bg-plum texture-grain" titleClass="text-highlight">
        <div className="grid sm:grid-cols-2 gap-3">
          {CHECKLIST.map((item, i) => (
            <Reveal key={item} delay={i * 0.06}>
              <div className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-2xl p-5 hover:bg-white/10 transition-colors duration-300">
                <span className="w-6 h-6 rounded-full bg-lilac flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-3.5 h-3.5 text-white" />
                </span>
                <p className="text-sm sm:text-base text-white/85 leading-relaxed">{item}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.3} className="mt-10 text-center">
          <Link
            to="/quiz"
            data-testid="treatment-quiz-cta"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-lilac text-white font-bold text-sm uppercase tracking-[0.14em] hover:bg-lilac-dark active:scale-95 transition-all duration-300"
          >
            Scopri se fa per te <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </Chapter>

      {/* COME SI APPLICA */}
      <div id="come-si-applica">
        <Chapter number="04" eyebrow="Il rituale" title="Come si applica il Trattamento Rapida Crescita?">
          <div className="grid md:grid-cols-3 gap-5">
            {STEPS.map((step, i) => (
              <Reveal key={step.title} delay={i * 0.1}>
                <div className="relative bg-mist rounded-3xl p-5 h-full overflow-hidden">
                  <img
                    src={step.img}
                    alt={`Passo ${i + 1}: ${step.title}`}
                    loading="lazy"
                    className="w-full aspect-square object-cover rounded-2xl"
                    data-testid={`step-img-${i + 1}`}
                  />
                  <div className="p-2 pt-5">
                    <p className="uppercase text-xs font-bold tracking-[0.22em] text-matcha">Step {i + 1}</p>
                    <h3 className="mt-2 font-serif text-2xl text-plum">{step.title}</h3>
                    <p className="mt-3 text-sm text-plum/60 leading-relaxed relative">{step.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.25}>
            <div className="mt-8 bg-lilac-soft border border-lilac-light rounded-3xl p-7 flex flex-col sm:flex-row sm:items-center gap-6">
              <div className="shrink-0">
                <p className="uppercase text-xs font-bold tracking-[0.22em] text-matcha">Frequenza</p>
                <p className="font-serif text-3xl text-plum mt-1">2 volte a settimana</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-2 flex-1">
                {TIPS.map((tip) => (
                  <p key={tip} className="text-sm text-plum/70 flex items-center gap-2">
                    <Check className="w-4 h-4 text-matcha shrink-0" /> {tip}
                  </p>
                ))}
              </div>
            </div>
          </Reveal>
        </Chapter>
      </div>

      {/* COSTANZA */}
      <Chapter number="05" eyebrow="Il percorso" title="La costanza fa la differenza" className="bg-lilac-soft/50">
        <Reveal>
          <p className="text-base sm:text-lg text-plum/70 leading-relaxed max-w-3xl">
            La cura dei tuoi capelli è un percorso, non un gesto singolo. Inserisci il trattamento nella tua
            routine e utilizzalo con costanza seguendo le modalità d'uso consigliate: è la regolarità,
            settimana dopo settimana, che permette al cuoio capelluto di ritrovare il suo equilibrio.
          </p>
        </Reveal>
        <div className="mt-8 grid sm:grid-cols-3 gap-4">
          {kits.map((kit, i) => (
            <ProductCard key={kit.slug} product={kit} index={i} />
          ))}
        </div>
      </Chapter>

      {/* PRIMA / DOPO */}
      <ResultsSection />

      {/* RECENSIONI */}
      <Chapter number="06" eyebrow="Le vostre esperienze" title="Oltre 53 donne hanno già scelto Rapida Crescita">
        <Reveal>
          <div className="bg-mist rounded-3xl p-8 sm:p-12 text-center" data-testid="reviews-teaser">
            <div className="flex justify-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 text-lilac fill-lilac" />
              ))}
            </div>
            <p className="mt-5 font-serif text-xl sm:text-2xl text-plum max-w-xl mx-auto leading-relaxed">
              Stiamo raccogliendo le esperienze di chi ha iniziato il percorso. Le recensioni verificate
              delle clienti appariranno qui.
            </p>
            <Link
              to="/recensioni"
              data-testid="treatment-reviews-cta"
              className="inline-flex items-center gap-2 mt-7 px-8 py-4 rounded-full border-2 border-plum/15 text-plum font-bold text-sm uppercase tracking-[0.14em] hover:border-lilac-dark hover:text-matcha transition-all duration-300"
            >
              Leggi tutte le recensioni
            </Link>
          </div>
        </Reveal>
      </Chapter>

      {/* FORMATO & FORMULA */}
      <Chapter number="07" eyebrow="Dettagli" title="Formato e formula" className="bg-mist/60">
        <div className="grid md:grid-cols-2 gap-5">
          <Reveal>
            <div className="bg-white rounded-3xl p-8 h-full border border-lilac-light">
              <Droplets className="w-6 h-6 text-matcha" />
              <h3 className="mt-4 font-serif text-xl text-plum">Formato</h3>
              <p className="mt-3 text-sm sm:text-base text-plum/60 leading-relaxed">
                Flacone da 50 ml con contagocce per un'applicazione precisa sul cuoio capelluto.
                Durata indicativa: circa 1 mese con utilizzo regolare.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-white rounded-3xl p-8 h-full border border-lilac-light">
              <Leaf className="w-6 h-6 text-matcha" />
              <h3 className="mt-4 font-serif text-xl text-plum">La formula</h3>
              <ul className="mt-3 space-y-2">
                {FORMULA.map((f) => (
                  <li key={f} className="text-sm sm:text-base text-plum/60 flex items-center gap-2">
                    <Check className="w-4 h-4 text-matcha shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </Chapter>

      {/* CTA FINALE */}
      <section className="px-6 pb-20 pt-4">
        <Reveal>
          <div className="max-w-5xl mx-auto bg-lilac rounded-[2.5rem] p-10 sm:p-16 text-center texture-grain relative overflow-hidden">
            <Sprout className="w-8 h-8 text-highlight mx-auto" />
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-highlight tracking-tight">
              Pronta a prenderti cura dei tuoi capelli?
            </h2>
            <p className="mt-4 text-white/85 text-base sm:text-lg max-w-xl mx-auto">
              Scopri il Trattamento Rapida Crescita e inseriscilo nella tua routine di cura del cuoio capelluto.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
              <button
                data-testid="treatment-final-buy"
                onClick={() => document.querySelector(`[data-testid="buy-box-${product.slug}"] [data-testid="buybox-add-btn"]`)?.click()}
                className="px-8 py-4 rounded-full bg-white text-plum font-bold text-sm uppercase tracking-[0.14em] hover:scale-105 active:scale-95 transition-transform duration-300"
              >
                Acquista ora
              </button>
              <a
                href="#come-si-applica"
                data-testid="treatment-final-how"
                className="px-8 py-4 rounded-full border-2 border-white/60 text-white font-bold text-sm uppercase tracking-[0.14em] hover:bg-white/10 transition-colors duration-300"
              >
                Scopri come utilizzarlo
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
